import { useEffect, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useCreateLeadLostReason } from '../../hooks/useLeads';
import type { LeadLostReason } from '../../services/api/leads';

export interface LostReasonResult {
  lostReasonId: number;
  lostReasonNote: string | null;
}

interface LostReasonModalProps {
  isOpen: boolean;
  /** The lead being marked lost, for the heading. */
  leadName: string;
  reasons: LeadLostReason[];
  /** Pre-selected when the reason is being edited rather than first set. */
  initialReasonId?: number | null;
  initialNote?: string | null;
  isSaving?: boolean;
  onCancel: () => void;
  onConfirm: (result: LostReasonResult) => void;
}

/**
 * MKS-347 — "why did we lose it?", asked the moment a deal is moved to the
 * lost column.
 *
 * The list is the `lead_lost_reasons` registry, which sales extend from right
 * here (the same add-inline affordance the source and label pickers have) —
 * there is no admin page to visit first. The free-text note is optional; the
 * reason itself is not, which is also enforced server-side so an API client
 * cannot skip it.
 *
 * The caller owns the move: this modal only ever reports a choice, and the
 * drag that opened it is applied by `onConfirm`, never before.
 */
export default function LostReasonModal({
  isOpen,
  leadName,
  reasons,
  initialReasonId = null,
  initialNote = null,
  isSaving = false,
  onCancel,
  onConfirm,
}: LostReasonModalProps) {
  const [reasonId, setReasonId] = useState<number | null>(initialReasonId);
  const [note, setNote] = useState(initialNote ?? '');
  const [newReason, setNewReason] = useState('');
  const [adding, setAdding] = useState(false);
  const createReason = useCreateLeadLostReason();

  // Reset every time the modal is opened for a (possibly different) lead, so
  // the previous deal's reason is never pre-filled onto the next one.
  useEffect(() => {
    if (!isOpen) return;
    setReasonId(initialReasonId);
    setNote(initialNote ?? '');
    setNewReason('');
    setAdding(false);
  }, [isOpen, initialReasonId, initialNote]);

  const handleAddReason = () => {
    const name = newReason.trim();
    if (!name) return;
    createReason.mutate(name, {
      onSuccess: (created) => {
        // Select what was just added, so adding a reason IS choosing it.
        setReasonId(created.data.id);
        setNewReason('');
        setAdding(false);
      },
    });
  };

  const handleConfirm = () => {
    if (reasonId === null) return;
    onConfirm({ lostReasonId: reasonId, lostReasonNote: note.trim() || null });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="Why was this deal lost?"
      size="sm"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            // The reason is mandatory; the note is not.
            disabled={reasonId === null}
            isLoading={isSaving}
          >
            Mark as lost
          </Button>
        </div>
      }
    >
      <p className="mb-3 text-sm text-gray-600">
        Moving <span className="font-medium text-gray-900">{leadName}</span> to the lost column.
      </p>

      <fieldset className="space-y-1.5">
        <legend className="mb-1 text-sm font-medium text-gray-700">Reason</legend>
        {reasons.map((reason) => (
          <label key={reason.id} className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="lostReason"
              value={reason.id}
              checked={reasonId === reason.id}
              onChange={() => setReasonId(reason.id)}
              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            {reason.name}
          </label>
        ))}
        {reasons.length === 0 && !adding && (
          <p className="text-sm text-gray-500">No reasons configured yet — add the first one.</p>
        )}
      </fieldset>

      {adding ? (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            value={newReason}
            autoFocus
            aria-label="New lost reason"
            placeholder="New reason"
            onChange={(e) => setNewReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddReason();
              }
            }}
            className="flex-1 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <Button
            variant="secondary"
            onClick={handleAddReason}
            isLoading={createReason.isPending}
            disabled={!newReason.trim()}
          >
            Add
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 text-xs text-primary-600 hover:text-primary-700"
        >
          + Add a reason
        </button>
      )}

      <label className="mt-4 block text-sm font-medium text-gray-700">
        Note <span className="font-normal text-gray-400">(optional)</span>
        <textarea
          value={note}
          rows={3}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </label>
    </Modal>
  );
}
